import './Chart.css'
import {
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import { Line } from 'react-chartjs-2'

import { cn } from '@sparkai/utils'

import {
  ChartValueClickEventProps,
  LineChartOptions,
  LineChartPluginsType,
  LineChartProps,
} from '../Charts.types'
import {
  DATA_OPTION,
  DATA_WEEKLY_OPTION,
  deepMergeChartOptions,
  DEFAULT_LINE_OPTIONS,
  LINEAR_SCALE_LINE_OPTIONS,
  LINEAR_SCALE_LINE_WEEKLY_OPTIONS,
  MULTIPLE_LINE_OPTIONS,
  PERCENT_UNIT_ACCURACY_LINE_OPTIONS,
  PERCENT_UNIT_DAILY_ACCURACY_LINE_OPTIONS,
  PERCENT_UNIT_DAILY_LINE_OPTIONS,
  PERCENT_UNIT_LINE_OPTIONS,
  PREDICTION_LINE_OPTIONS,
  SECONDS_UNIT_DAILY_LINE_OPTIONS,
  SECONDS_UNIT_LINE_OPTIONS,
  STACKED_DAILY_LINE_OPTIONS_WITH_DATE,
  STACKED_LINE_OPTIONS_WITH_DATE,
  STACKED_LINE_OPTIONS_WITH_DATE_WEEKLY,
  STACKED_LINE_OPTIONS_WITH_HOURS,
  TIME_TO_COMPLETE_DAILY_OPTIONS,
  TIME_TO_COMPLETE_OPTIONS,
  TIME_TO_COMPLETE_WEEKLY_OPTIONS,
} from '../configs/options'
import {
  PULSE_LAST_POINT_PLUGIN,
  VERTICAL_LINE_TOOLTIP_PLUGIN,
  VERTICAL_MULTILINE_TOOLTIP_PLUGIN,
} from '../configs/plugins'
import { getNearestClickedDatasetIndex, setMinYScale } from '../utils'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  annotationPlugin
)

function addAnnotation(
  options: ChartOptions,
  targetConstant: number,
  targetConstantLabel: string,
  labelPosition: 'top' | 'bottom' = 'top'
) {
  const annotation = {
    annotations: {
      line1: {
        type: 'line',
        borderColor: 'rgba(107, 114, 128, 1)',
        borderDash: [4, 4],
        borderWidth: 2,
        yMin: targetConstant,
        yMax: targetConstant,
        label: {
          backgroundColor: '#ff000000',
          color: 'rgba(107, 114, 128, 1)',
          content: targetConstantLabel,
          display: true,
          position: 'start',
          xAdjust: -12,
          yAdjust: labelPosition === 'top' ? -12 : 5,
          font: 'Inter',
        },
      },
    },
  }

  /* eslint-disable-next-line */
  /* @ts-ignore */
  options.plugins = { ...options.plugins, annotation }
}

function determineChartType(option: LineChartOptions, data?: ChartData<'line'>) {
  switch (option) {
    case 'stacked-date':
      return STACKED_LINE_OPTIONS_WITH_DATE
    case 'stacked-date-weekly':
      return STACKED_LINE_OPTIONS_WITH_DATE_WEEKLY()
    case 'stacked-date-daily':
      return STACKED_DAILY_LINE_OPTIONS_WITH_DATE
    case 'stacked-hour':
      return STACKED_LINE_OPTIONS_WITH_HOURS
    case 'percent-unit':
      return PERCENT_UNIT_LINE_OPTIONS
    case 'percent-unit-accuracy':
      return PERCENT_UNIT_ACCURACY_LINE_OPTIONS
    case 'percent-unit-daily':
      return PERCENT_UNIT_DAILY_LINE_OPTIONS
    case 'percent-unit-daily-accuracy':
      return PERCENT_UNIT_DAILY_ACCURACY_LINE_OPTIONS
    case 'seconds-unit':
      return SECONDS_UNIT_LINE_OPTIONS
    case 'seconds-unit-daily':
      return SECONDS_UNIT_DAILY_LINE_OPTIONS
    case 'multiline':
      return MULTIPLE_LINE_OPTIONS
    case 'prediction-line':
      return PREDICTION_LINE_OPTIONS
    case 'data':
      return DATA_OPTION
    case 'data-weekly':
      return DATA_WEEKLY_OPTION()
    case 'linear-scale': {
      const config = LINEAR_SCALE_LINE_OPTIONS
      if (!data) return config
      return setMinYScale(config, data?.datasets)
    }
    case 'linear-scale-weekly': {
      const config = LINEAR_SCALE_LINE_WEEKLY_OPTIONS()
      if (!data) return config
      return setMinYScale(config, data?.datasets)
    }
    case 'time-to-complete':
      return TIME_TO_COMPLETE_OPTIONS

    case 'time-to-complete-weekly':
      return TIME_TO_COMPLETE_WEEKLY_OPTIONS()

    case 'time-to-complete-daily':
      return TIME_TO_COMPLETE_DAILY_OPTIONS

    default:
      return DEFAULT_LINE_OPTIONS
  }
}

function addPlugins(plugins: Array<LineChartPluginsType> | undefined) {
  if (!plugins) return []

  const values = {
    'tooltip-vertical-multiline': VERTICAL_MULTILINE_TOOLTIP_PLUGIN,
    'tooltip-vertical-line': VERTICAL_LINE_TOOLTIP_PLUGIN,
    'pulse-last-point': PULSE_LAST_POINT_PLUGIN,
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const list: any[] = []
  plugins.forEach((plugin) => list.push(values[plugin]))

  return list
}

export function LineChart({
  className = '',
  option = 'default',
  optionOverride,
  datasets,
  labels,
  targetConstant,
  targetConstantLabel,
  targetConstantLabelPosition = 'top',
  customLegend,
  plugins,
  onValueClick,
}: LineChartProps) {
  const _data: ChartData<'line'> = { labels, datasets }

  const selectedOption = {
    ...determineChartType(option, _data),
    onClick: (event: ChartValueClickEventProps, ctx?: { index?: number }[]) => {
      if (ctx && ctx.length > 0 && ctx[0].index !== undefined) {
        const label = event.chart.config.data.labels[ctx[0].index]
        const value = event.chart.config.data.datasets[0].data[ctx[0].index]
        onValueClick?.(label, value, getNearestClickedDatasetIndex(event))
      }
    },
  }

  const _options = Object.assign(
    {
      interaction: {
        intersect: false,
        mode: 'index',
      },
    },
    selectedOption
  ) as unknown as ChartOptions<'line'>

  if (targetConstant)
    addAnnotation(_options, targetConstant, targetConstantLabel ?? 'Target Time', targetConstantLabelPosition)

  const referencePlugins = addPlugins(plugins)

  const finalOptions = deepMergeChartOptions<'line'>(_options, optionOverride)

  return (
    <>
      <Line
        className={cn('LineChart h-[232px] max-h-[232px]', className)}
        plugins={referencePlugins}
        options={finalOptions}
        data={_data}
      />
      {customLegend && (
        <div className='mt-3 mr-4'>
          <ul className='flex w-full flex-wrap justify-end gap-4'>
            {datasets.map((data, key) => (
              <div className='flex items-center gap-2' key={key}>
                <div className='h-2 w-2' style={{ backgroundColor: data?.backgroundColor ?? '#00A6FF' }} />
                <li className='font-inter text-xs font-medium text-[#6B7280]'>
                  {data?.label?.toUpperCase() ?? 'demo'}
                </li>
              </div>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}
